import React, { useMemo, useRef, useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { useFeedData } from '../../data/queries/feedData';
import { closedReports, reportToMarkdown, type IncidentReport } from '../../lib/incidentReport';
import { formatDurationMs } from '../../lib/funnel';
import { formatUtcDateTime } from '../../lib/time';
import EmptyState from '../../ui/EmptyState';
import ErrorState from '../../ui/ErrorState';
import SidePanel from '../../ui/SidePanel';
import Skeleton from '../../ui/Skeleton';
import { SEVERITY_CLASS } from './FeedBits';

/** Keeps a report as a Markdown file on the person's computer. */
function download(report: IncidentReport) {
  const url = URL.createObjectURL(new Blob([reportToMarkdown(report)], { type: 'text/markdown' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `incident-report-${report.label}.md`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * The Incident Reports (client notes 10/26, Settings 1d): one for each campaign or incident that is closed. The backend does
 * not write them, so they are written here from the records of the feed (`lib/incidentReport.ts`) and appear as soon as a
 * row reaches Closed; each can be read and kept as a file.
 */
const IncidentReports: React.FC = () => {
  const { rows, loading, error, refresh } = useFeedData();
  const reports = useMemo(() => closedReports(rows), [rows]);
  const [openId, setOpenId] = useState<string | null>(null);
  const open = reports.find((report) => report.id === openId) ?? null;
  // The panel slides out after it is closed: it keeps showing the report it had until it is gone.
  const lastOpen = useRef<IncidentReport | null>(null);
  if (open) lastOpen.current = open;
  const shown = open ?? lastOpen.current;

  if (error) {
    return (
      <ErrorState title="Could not load the incident reports" onRetry={() => void refresh()}>
        Nothing was changed. Try again in a moment.
      </ErrorState>
    );
  }
  if (loading && reports.length === 0) return <Skeleton lines={3} label="Loading the incident reports…" />;
  if (reports.length === 0) {
    return (
      <EmptyState title="No incident reports yet" icon={FileText}>
        One is written here as soon as a campaign or an incident is closed.
      </EmptyState>
    );
  }

  return (
    <>
      <ul className="c3-report-list" aria-label="Incident reports">
        {reports.map((report) => (
          <li key={report.id}>
            <button type="button" className="c3-report-row" onClick={() => setOpenId(report.id)}>
              <span className="c3-report-id">{report.label}</span>
              <span className="c3-report-title">{report.title}</span>
              <span className={`tf-sev ${SEVERITY_CLASS[report.severity]}`}>{report.severity}</span>
              <span className="c3-report-meta">Closed {formatUtcDateTime(report.closedMs)}</span>
              <span className="c3-report-meta">{formatDurationMs(report.timeToCloseMs)} to close</span>
            </button>
          </li>
        ))}
      </ul>

      <SidePanel
        open={open != null}
        onClose={() => setOpenId(null)}
        title="Incident Report"
        subtitle={shown ? `${shown.label} · ${shown.title}` : undefined}
        icon={<FileText size={16} strokeWidth={1.5} aria-hidden className="text-c3-purple" />}
        closeLabel="Close the report"
        actions={
          shown ? (
            <button type="button" className="c3-btn c3-btn-sm inline-flex items-center gap-2" onClick={() => download(shown)}>
              <Download size={16} strokeWidth={1.5} aria-hidden />
              Download
            </button>
          ) : null
        }
      >
        {shown ? <ReportBody report={shown} /> : <span />}
      </SidePanel>
    </>
  );
};

const ReportBody: React.FC<{ report: IncidentReport }> = ({ report }) => (
  <div className="c3-report-body">
    <dl className="c3-report-facts">
      <div>
        <dt>Severity</dt>
        <dd>{report.severity}</dd>
      </div>
      <div>
        <dt>Type</dt>
        <dd>{report.kind === 'campaign' ? 'Campaign' : 'Incident'}</dd>
      </div>
      <div>
        <dt>Remediated</dt>
        <dd>{report.action || '—'}</dd>
      </div>
      <div>
        <dt>Time to close</dt>
        <dd>{formatDurationMs(report.timeToCloseMs)}</dd>
      </div>
      <div>
        <dt>First alert</dt>
        <dd>{formatUtcDateTime(report.receivedMs)}</dd>
      </div>
      <div>
        <dt>Closed</dt>
        <dd>{formatUtcDateTime(report.closedMs)}</dd>
      </div>
      <div className="is-wide">
        <dt>Sources</dt>
        <dd>{report.sources.join(', ') || '—'}</dd>
      </div>
      <div className="is-wide">
        <dt>Assets affected</dt>
        <dd>{report.assets.join(', ') || '—'}</dd>
      </div>
    </dl>

    {report.description ? (
      <section>
        <h4 className="c3-report-head">Summary</h4>
        <p>{report.description}</p>
      </section>
    ) : null}

    <section>
      <h4 className="c3-report-head">{report.kind === 'campaign' ? 'Incidents' : 'Remediation'}</h4>
      {report.incidents.map((incident) => (
        <div key={incident.label} className="c3-report-incident">
          <div className="c3-report-incident-title">
            <span className="c3-report-id">{incident.label}</span> {incident.title}
          </div>
          <div className="c3-report-meta">
            {incident.severity} · {incident.action}
          </div>
          {incident.steps.length > 0 ? (
            <ol>
              {incident.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          ) : (
            <p className="c3-report-meta">No steps were recorded.</p>
          )}
        </div>
      ))}
    </section>

    <section>
      <h4 className="c3-report-head">Timeline</h4>
      {report.timeline.length === 0 ? (
        <p className="c3-report-meta">No times were recorded.</p>
      ) : (
        <ul className="c3-report-timeline">
          {report.timeline.map((event, index) => (
            <li key={index}>
              <span className="c3-report-time">{formatUtcDateTime(event.atMs)}</span>
              <span>{event.text}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  </div>
);

export default IncidentReports;
